import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { protectedSegments } from './ticker_protection';

export function MarkedText({ text = '', tickers = [], keywords = [] }) {
    return protectedSegments(text, tickers, keywords).map((segment, index) => segment.ticker
        ? <mark key={index} className={segment.kind === 'keywords' ? 'push-keyword-mark' : 'push-symbol-mark'}>{segment.text}</mark>
        : <span key={index}>{segment.text}</span>);
}

// Keep native input selection, caret and undo; paint the marks in an aligned
// layer underneath, so no HTML ever enters the stored notification text.
export function MarkedInput({ value = '', onChange, label, tickers, keywords, multiline = false, maxLength, disabled }) {
    const input = useRef(null);
    const width = useRef(null);
    const [scroll, setScroll] = useState({ left: 0, top: 0 });
    const syncScroll = useCallback(() => { if (input.current) setScroll({ left: input.current.scrollLeft, top: input.current.scrollTop, gutter: input.current.offsetWidth - input.current.clientWidth }); }, []);
    const resize = useCallback(() => {
        if (multiline && input.current) {
            input.current.style.height = 'auto';
            input.current.style.height = `${Math.max(74, Math.min(input.current.scrollHeight, 162))}px`;
        }
        syncScroll();
    }, [multiline, syncScroll]);
    useEffect(() => {
        if (!globalThis.ResizeObserver || !input.current) return;
        const observer = new ResizeObserver(() => {
            const nextWidth = input.current?.offsetWidth;
            if (nextWidth !== width.current) { width.current = nextWidth; resize(); }
            else syncScroll();
        });
        observer.observe(input.current);
        return () => observer.disconnect();
    }, [resize, syncScroll]);
    useLayoutEffect(resize, [value, resize]);
    const props = { ref: input, 'aria-label': label, value, maxLength, disabled, spellCheck: false, onScroll: syncScroll, onChange: (event) => onChange(event.target.value) };
    return <div>
        <div className={`push-marked-input${multiline ? ' push-marked-multiline' : ''}${disabled ? ' push-marked-disabled' : ''}`}>
            <div className="push-marked-backdrop" aria-hidden="true" style={{ right: scroll.gutter || 0 }}><div className="push-marked-text" style={{ transform: `translate(${-scroll.left}px, ${-scroll.top}px)` }}>
                <MarkedText text={value} tickers={tickers} keywords={keywords} />{multiline && value.endsWith('\n') ? '\n' : ''}
            </div></div>
            {multiline ? <textarea {...props} rows={3} /> : <input {...props} type="text" />}
        </div>
        <div className="push-source-count" aria-hidden="true">{value.length} / {maxLength}</div>
    </div>;
}

export function SourceMarks({ campaign, disabled, onRemove }) {
    const groups = [
        ['protectedTitleTickers', '标题 · ticker', 'blue'], ['protectedBodyTickers', '正文 · ticker', 'blue'],
        ['protectedTitleKeywords', '标题 · 保留原关键词', 'purple'], ['protectedBodyKeywords', '正文 · 保留原关键词', 'purple'],
    ];
    if (!groups.some(([key]) => campaign[key]?.length)) return null;
    return <div className="push-source-marks" aria-label="原文标记">
        <Typography.Text type="secondary">已标记（点击 × 移除）</Typography.Text>
        <Space wrap size={[0, 8]}>{groups.flatMap(([key, label, color]) => (campaign[key] || []).map((text) => <Tag key={`${key}:${text}`} color={color}>
            {label} · {text}<Button type="text" size="small" icon={<CloseOutlined />} disabled={disabled} aria-label={`移除${label}标记 ${text}`} onClick={() => onRemove(key, text)} />
        </Tag>))}</Space>
    </div>;
}
